import { Component, h, Listen, Prop, Element, Method } from '@stencil/core';
import { matchMediaDesktop, matchMediaMobile } from '../utils';
import { showFoundationStyles, showRGCStyles } from '../../component-utils/stencilUtils';

export declare interface NavItem {
  text: string;
  link: string;
  isRestricted: boolean;
}

export declare interface NavigationRefs {
  secondaryNav: HTMLAafpSecondaryNavElement;
  tertiaryNav: HTMLElement;
  globalNavList: HTMLUListElement;
  globalNavListItem: HTMLLIElement;
  globalNavListItems: HTMLLIElement[];
}

declare global {
  interface Window {
    dataLayer: any;
  }
}

@Component({
  tag: 'aafp-nav-item',
  shadow: false,
})
export class AafpNavItem {
  @Prop({ mutable: true }) navIndex: string = '';
  @Prop({ mutable: true }) navItems: string = '';
  @Prop({ mutable: true }) navFlyout: string = '{}';
  @Prop({ mutable: true }) navType: string = '';
  @Prop({ mutable: true }) navLink: string = '';
  @Prop({ mutable: true }) navTitle: string = '';
  @Prop({ mutable: true }) dataMobileMenu: string = '';
  @Element() element: HTMLElement;
  refs: Partial<NavigationRefs> = {};
  /**
   * Toggles adl-nav-item--focus state for determining whether to show or hide secondaryNav
   */
  @Listen('click') // Handles Click Events
  primaryNavClick(event: Event) {
    const globalNavLink = this.element.querySelector('.adl-global-nav__link') as HTMLAnchorElement;
    const hasSecondaryMenu = globalNavLink && !globalNavLink.hasAttribute('href');
    const hasNavItems = !!this.navItems && !!JSON.parse(this.navItems).length;
    const anchorLink = this.element.querySelector('a');

    if (!hasSecondaryMenu && anchorLink !== null && !hasNavItems && window.dataLayer !== undefined) {
      const linkText = anchorLink.innerText.split('\n')[0];
      window.dataLayer.push({
        event: 'navigation',
        section: 'header navigation',
        link_text: linkText,
        link_url: anchorLink.href,
      });
    }

    if (this.navType === 'tertiary') {
      window.localStorage.setItem('prop31', `top:${this.element.innerText}`);
      return;
    }

    if (matchMediaMobile()) {
      // Checks for Mobile
      if (this.navType === 'global') {
        if (hasSecondaryMenu) {
          this.onClickGlobalNavItem(event);
        }
        if (this.isJumpTarget(globalNavLink)) {
          const button = document.querySelector('.adl-global-nav__menu-toggle') as HTMLButtonElement;
          this.triggerMobileMenuClickEvent(button);
        }
      }
      if (this.navType === 'secondary') {
        if (hasNavItems) {
          event.preventDefault();
          return !this.element.hasAttribute('data-tertiary-menu-active')
            ? this.toggleTertiaryMenu('set', true)
            : this.toggleTertiaryMenu('remove', true);
        } else {
          window.localStorage.setItem('prop31', `top:${this.element.innerText}`);
        }
        if (this.isJumpTarget(this.element.querySelector('a'))) {
          const button = document.querySelector('.adl-global-nav__menu-toggle') as HTMLButtonElement;
          this.triggerMobileMenuClickEvent(button);
        }
      }
    } else {
      if (this.navType === 'secondary') {
        if (hasNavItems) {
          event.preventDefault();
          return !this.element.hasAttribute('data-tertiary-menu-active')
            ? this.toggleTertiaryMenu('set', false)
            : this.toggleTertiaryMenu('remove', false);
        } else {
          window.localStorage.setItem('prop31', `top:${this.element.innerText}`);
        }
        if (this.isJumpTarget(this.element.querySelector('a'))) {
          this.toggleSecondaryMenu('remove', false);
        }
      }
      if (this.navType === 'global' && hasSecondaryMenu) {
        event.preventDefault();
        this.element.classList.toggle('adl-nav-item--focus');
        if (document.getElementsByClassName('adl-nav-item--focus').length > 1) {
          // Checks for an open menu to handle transition
          this.removeAllFocus();
          this.removeFadein(); // Removes Fadein animation styles to prevent conflict with transition
          this.refs.secondaryNav!.classList.add('adl-secondary-nav__fade-transition');
          this.element.classList.toggle('adl-nav-item--focus'); // Fadeout Fast
          setTimeout(() => {
            this.refs.secondaryNav!.classList.remove('adl-secondary-nav__fade-transition'); // Remove Fadeout Fast for Fadein
            document
              .querySelector('.adl-nav-item--tertiary-menu-active')
              ?.classList.remove('adl-nav-item--tertiary-menu-active');
            document.querySelector('aafp-tertiary-nav')?.setAttribute('data-nav-items', '[]');
            this.toggleSecondaryMenu('set');
          }, 100);
        } else {
          this.element.classList.contains('adl-nav-item--focus') // Handles single menu click on/off
            ? this.toggleSecondaryMenu('set')
            : this.toggleSecondaryMenu('remove');
        }
      }
    }
  }

  /**
   * Ignore mobile events -
   * Toggle secondary and tertiary
   * menu attributes on desktop
   */
  @Listen('mouseenter')
  onMouseEnter() {
    if (matchMediaMobile()) {
      return;
    }
    this.element.classList.add('hover'); // Handles original hover behavior for secondary menu
  }

  @Listen('mouseleave')
  mouseLeaveHandler() {
    if (matchMediaMobile()) {
      return;
    }
    // The following constants check for the various mouseleave cases, closing the nav on leaves to logo and universal nav
    this.element.classList.remove('hover');
  }

  /**
   * Cleanup method ensures that tertiary menu will be removed
   * before opening secondary nav menu
   */
  closeMobileTertiaryMenu = (globalNavItem: HTMLLIElement | null) => {
    this.element.removeAttribute('data-tertiary-menu-active');
    this.element.classList.remove('adl-nav-item__tertiary-menu-active');
    (this.element.parentNode as HTMLElement).classList.remove('adl-secondary-nav-item__submenu--open');
    document.querySelector('.adl-secondary-nav__header-link')!.classList.toggle('adl-secondary-nav__header-link--hide'); // Hides Header Link in tertiary menu on mobile.  Removing this line will add the link ABOVE the tertiary menu view.
    if (!this.refs.tertiaryNav) {
      return;
    }

    Array.from((this.element.parentNode as HTMLElement).children)
      .filter((child: Element) => child.getAttribute('nav-index') !== this.navIndex)
      .forEach((el: Element) => el.classList.remove('adl-nav-item--inactive'));

    if (globalNavItem) {
      globalNavItem.classList.remove('adl-nav-item--inactive');
    }
  };

  removeAllFocus() {
    Array.from(document.getElementsByClassName('adl-nav-item--focus')).forEach((element) => {
      element.classList.remove('adl-nav-item--focus');
    });
  }

  removeFadein() {
    Array.from(document.getElementsByClassName('adl-secondary-nav__fadein')).forEach((element) => {
      element.classList.remove('adl-secondary-nav__fadein');
      this.resetAnimation();
      element.classList.add('adl-secondary-nav__fadeout');
    });
  }

  /**
   * Do not prevent default on desktop -
   * Open or close secondary menu on mobile
   */
  onClickGlobalNavItem = (event: Event) => {
    if (matchMediaDesktop()) {
      return;
    }

    event.preventDefault();
    return this.mobileSecondaryMenuIsOpen() ? this.onClickGlobalNavItemMobile() : this.toggleSecondaryMenu('set', true);
  };

  /**
   * Compare index of click event target
   * to open menu index on secondary nav -
   * close secondary menu if indices match
   */
  onClickGlobalNavItemMobile = () => {
    if (!this.refs.secondaryNav) {
      return;
    }
    if (this.refs.secondaryNav.getAttribute('data-item-index') === this.navIndex) {
      return this.toggleSecondaryMenu('remove', true);
    }
  };

  /**
   * Ensure that aria attributes are
   * updated whenever a new nav item
   * becomes active
   */
  resetAriaAttributes = (listEl: HTMLUListElement, liIndex: number) => {
    if (!listEl) return;
    Array.from(listEl.children).forEach((el: Element, index: number) => {
      if (index !== liIndex) {
        el.setAttribute('aria-expanded', 'false');
      }
    });
  };

  /**
   * Reset attributes to inactive state on nav item,
   * secondary nav component,
   * and global nav list items
   * @param secondaryNav
   */
  removeSecondaryMenu(secondaryNav: HTMLAafpSecondaryNavElement): void {
    try {
      if (this.element && this.element.parentElement) {
        this.element.parentElement.removeAttribute('data-menu-open');
        this.element.parentElement.classList.remove('adl-nav-item__submenu--open');
      }
      secondaryNav.removeAttribute('data-secondary-menu-active');
      secondaryNav.setAttribute('data-nav-items', '[]');
      secondaryNav.removeAttribute('data-item-index');
      if (this.refs.globalNavList) {
        secondaryNav.removeMobileSubmenuSlots();
        // Array.from(this.refs.globalNavList.children).forEach((child: Element) =>
        //   child.classList.remove('adl-nav-item--inactive'),
        // );
      }
    } catch (e) {
      return;
    }
  }

  resetAnimation() {
    // The following line allows the fade animations to retrigger.
    // Source: https://css-tricks.com/restart-css-animation/
    this.element.offsetWidth;
  }

  setBrandFlyoutContent = (navMenu: Element) => {
    navMenu.setAttribute('data-nav-flyout', this.navFlyout);
  };

  /**
   * Set active state attributes on
   * secondary nav component, close
   * any open tertiary menus,
   * and update brand nav flyout content
   * @param secondaryNav
   * @param isMobile
   */
  setSecondaryMenu(secondaryNav: HTMLAafpSecondaryNavElement, isMobile: boolean): void {
    secondaryNav.setAttribute('data-nav-items', this.navItems);
    secondaryNav.setAttribute('data-item-index', this.navIndex);
    secondaryNav.setAttribute('nav-header-link', this.navLink);
    secondaryNav.setAttribute('nav-header-title', this.navTitle);
    this.setBrandFlyoutContent(secondaryNav);
    if (isMobile) {
      return secondaryNav.setAttribute('data-secondary-menu-active', 'true');
    }
  }

  toggleSecondaryMenu = (actionType: 'set' | 'remove', isMobile: boolean = false) => {
    if (!this.refs.secondaryNav) {
      return;
    }
    if (actionType === 'set') {
      this.toggleTertiaryMenu('remove', false);
      this.updateGlobalMenuAria(Number(this.navIndex));
      this.setSecondaryMenu(this.refs.secondaryNav, isMobile);
      this.refs.secondaryNav.classList.remove('adl-secondary-nav__fadeout'); // Ensures no Fadeout
      this.resetAnimation();
      this.refs.secondaryNav.classList.add('adl-secondary-nav__fadein');
    }

    if (actionType === 'remove') {
      this.resetAriaAttributes(this.refs.globalNavList as HTMLUListElement, -1);
      this.removeAllFocus(); // Resets focus
      this.removeFadein(); // Removes Fadein and adds Fadeout
      setTimeout(() => {
        this.removeSecondaryMenu(this.refs.secondaryNav!);
      }, 200);
    } // Timer for animation purposes.
  };

  /**
   * Set aria expanded true on active global nav item
   * and reset all other nav items to aria expanded false
   */
  updateGlobalMenuAria = (activeMenuIndex: number) => {
    if (!this.refs.globalNavListItem) {
      return;
    }
    this.refs.globalNavListItem.setAttribute('aria-expanded', 'true');
    this.resetAriaAttributes(this.refs.globalNavList as HTMLUListElement, activeMenuIndex);
  };

  /**
   * Check secondary nav attribute to determine if menu is open
   */
  mobileSecondaryMenuIsOpen = (): boolean => {
    return this.refs.secondaryNav && this.refs.secondaryNav.hasAttribute('data-secondary-menu-active') ? true : false;
  };

  /**
   * Check secondary nav attribute to determine if menu is open
   */
  mobileTertiaryMenuIsOpen = (): boolean => {
    return this.refs.tertiaryNav && this.refs.tertiaryNav.hasAttribute('data-tertiary-menu-active') ? true : false;
  };

  /**
   * Hide global nav item and
   * iterate over secondary nav menu
   * items and hide inactive secondary
   * nav items while tertiary menu is open
   */
  openMobileTertiaryMenu = (globalNavItem?: Element | null) => {
    if (!globalNavItem) {
      return;
    }
    globalNavItem.classList.add('adl-nav-item--inactive');
    this.element.setAttribute('data-tertiary-menu-active', 'true');
    this.element.classList.toggle('adl-nav-item__tertiary-menu-active');
    (this.element.parentNode as HTMLElement).classList.toggle('adl-secondary-nav-item__submenu--open');
    document.querySelector('.adl-secondary-nav__header-link')!.classList.toggle('adl-secondary-nav__header-link--hide');
    Array.from((this.element.parentNode as HTMLElement).children)
      .filter((child: Element) => child.getAttribute('nav-index') !== this.navIndex)
      .forEach((el: Element) => el.classList.add('adl-nav-item--inactive'));
  };

  /**
   * Check global nav list items to find active global nav item
   */
  findOpenMenu = (globalNavListItems: HTMLLIElement[]): HTMLLIElement | null => {
    return globalNavListItems.find((child: HTMLLIElement) => child.hasAttribute('data-menu-open')) || null;
  };

  @Method()
  async closeMobileSubmenu() {
    const openMenu = this.findOpenMenu(this.refs.globalNavListItems as HTMLLIElement[]);
    if (openMenu) {
      const inactiveLink: any = openMenu.querySelector('.adl-nav-item--inactive');
      if (inactiveLink) {
        inactiveLink.classList.remove('adl-nav-item--inactive');
      }
      this.toggleSecondaryMenu('remove', true);
    }
  }

  setActiveTertiaryMenuNavItemSelector() {
    const alreadyActive = document.querySelector('.adl-nav-item--tertiary-menu-active');
    if (alreadyActive) {
      alreadyActive.classList.remove('adl-nav-item--tertiary-menu-active');
      alreadyActive.removeAttribute('data-tertiary-menu-active');
    }
    this.element.classList.add('adl-nav-item--tertiary-menu-active');
  }

  toggleTertiaryMenu = (actionType: 'set' | 'remove', isMobile: boolean = false) => {
    if (!this.refs.tertiaryNav) {
      return;
    }
    try {
      const parentNavItem: HTMLLIElement | null = this.findOpenMenu(this.refs.globalNavListItems as HTMLLIElement[]);
      if (actionType === 'set') {
        if (isMobile && parentNavItem) {
          this.openMobileTertiaryMenu(parentNavItem.querySelector('a[slot="nav-link"]'));
        }
        const renderColumns = !isMobile && !this.refs.secondaryNav!.hasAttribute('show-flyout');
        renderColumns
          ? this.refs.tertiaryNav.setAttribute('render-columns', '')
          : this.refs.tertiaryNav.removeAttribute('render-columns');
        this.element.setAttribute('data-tertiary-menu-active', 'true');
        this.setActiveTertiaryMenuNavItemSelector();
        this.refs.tertiaryNav.setAttribute('data-nav-items', this.navItems);
        this.refs.tertiaryNav.setAttribute('data-active-menu-index', this.navIndex);
        const firstAnchor = this.element.firstElementChild;
        if (firstAnchor) {
          this.refs.tertiaryNav.setAttribute(
            'nav-secondary-title',
            firstAnchor!.getAttribute('nav-secondary-title') || '',
          );
          this.refs.tertiaryNav.setAttribute(
            'nav-secondary-link',
            firstAnchor!.getAttribute('nav-secondary-link') || '',
          );
        }
      }

      if (actionType === 'remove') {
        this.element.removeAttribute('data-tertiary-menu-active');
        this.element.classList.remove('adl-nav-item--tertiary-menu-active');
        this.refs.tertiaryNav.removeAttribute('data-nav-items');
        this.refs.tertiaryNav.removeAttribute('data-active-menu-index');
        if (isMobile && parentNavItem) {
          this.closeMobileTertiaryMenu(parentNavItem.querySelector('a[slot="nav-link"]'));
        }
      }
    } catch (e) {
      return;
    }
  };

  /**
   * Verify that an index is a valid number before comparing index values
   */
  isValid = (index: number): boolean => {
    return !isNaN(index);
  };

  /**
   * Set dom refs to all navigation components
   */
  setNavMenuRefs() {
    const globalNav = document.querySelector('aafp-global-nav');

    if (globalNav && globalNav.querySelector('ul')) {
      this.refs.globalNavList = globalNav.querySelector('ul') as HTMLUListElement;
    }

    if (this.refs.globalNavList) {
      this.refs.globalNavListItems = Array.from(this.refs.globalNavList.children) as HTMLLIElement[];
    }

    if (document.querySelector('aafp-secondary-nav')) {
      this.refs.secondaryNav = document.querySelector('aafp-secondary-nav') as HTMLAafpSecondaryNavElement;
    }

    if (document.querySelector('aafp-tertiary-nav')) {
      this.refs.tertiaryNav = document.querySelector('aafp-tertiary-nav') as HTMLElement;
    }
  }

  private getHref(anchorTag: HTMLAnchorElement): string {
    return anchorTag.getAttribute('href') as string;
  }

  private isJumpTarget(anchorTag: HTMLAnchorElement | null) {
    return anchorTag ? /#/.test(this.getHref(anchorTag)) : false;
  }

  private triggerMobileMenuClickEvent(button: HTMLButtonElement) {
    return button.click();
  }

  resetGlobalNavItemClassList() {
    const link = this.refs.globalNavListItem!.querySelector('.adl-nav-item--inactive');
    if (link) {
      link.classList.remove('adl-nav-item--inactive');
    }
  }

  setGlobalNavParentRef() {
    const index = parseInt(this.navIndex, 10);
    if (this.isValid(index) && this.refs.globalNavList) {
      const liItems = this.refs.globalNavList.children;
      if (liItems) {
        this.refs.globalNavListItem = this.refs.globalNavList.children[index] as HTMLLIElement;
        const isOpen = this.refs.globalNavListItem && this.refs.globalNavListItem.hasAttribute('data-menu-open');
        if (matchMediaDesktop() && isOpen) {
          this.resetGlobalNavItemClassList();
        }
      }
    }
  }

  componentWillLoad() {
    if (showFoundationStyles(document)) {
      this.element.classList.add('adl-foundation-nav-item');
    } else if (showRGCStyles(document)) {
      this.element.classList.add('adl-rgc-nav-item');
    }
  }

  componentDidLoad() {
    this.setNavMenuRefs();
    this.setGlobalNavParentRef();
  }

  render() {
    return <slot name="nav-link"></slot>;
  }
}
